"use client";
import { useEffect, useMemo, useState } from "react";
import * as SVG from "@/svgs";
import Link from "next/link";
import Fuse from "fuse.js";
import { FarmDirectoryPageProps, FarmProps, FarmMetaProps } from "./page";
import { SvgSelector } from "@/components/SvgSelector";
import { CaretDownIcon } from "@phosphor-icons/react";

const TypeItem = ({
  color,
  svg,
  title,
  last = false,
  _id,
  onClick,
  active,
}: {
  color: string;
  _id: string;
  title: string;
  last?: boolean;
  svg: keyof typeof SVG;
  onClick: (id: string) => void;
  active: boolean;
}) => {
  return (
    <button
      onClick={() => onClick(_id)}
      className={`filterMenuItem cursor-pointer flex items-center py-2 gap-4 w-full${
        last ? "" : " border-b-[2px]"
      } border-b-secondary hover:bg-secondary ${
        active ? "active bg-tertiary" : ""
      }`}
    >
      <SvgSelector name={svg} className="w-8 max-h-8" />
      <span className={`uppercase font-display text-[25px] ${color}`}>
        {title}
      </span>
    </button>
  );
};

const FilterTag = ({
  label,
  color = "bg-primaryDark",
  onClick,
}: {
  label: string;
  color?: string;
  onClick: () => void;
}) => (
  <button
    onClick={onClick}
    className={`${color} text-white px-4 py-1 rounded-full text-sm font-bold hover:bg-primaryDark`}
  >
    {label} ✕
  </button>
);

export const Results = ({ sidebar, items }: FarmDirectoryPageProps) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [filteredItems, setFilteredItems] = useState<FarmProps[]>(items);
  const [selectedCategoryIds, setSelectedCategoryIds] = useState<string[]>([]);
  const [selectedTypeIds, setSelectedTypeIds] = useState<string[]>([]);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  const fuse = useMemo(() => {
    return new Fuse(items, {
      keys: ["title", "county", "type.title", "categories.title"],
      threshold: 0.3,
    });
  }, [items]);

  useEffect(() => {
    let result: FarmProps[] = [];

    if (searchQuery.trim()) {
      result = fuse.search(searchQuery.trim()).map((r) => r.item);
    } else {
      result = [...items];
    }

    if (selectedCategoryIds.length > 0) {
      result = result.filter((item) =>
        item.categories.some((cat) => selectedCategoryIds.includes(cat._id))
      );
    }

    if (selectedTypeIds.length > 0) {
      result = result.filter((item) => selectedTypeIds.includes(item.type._id));
    }

    setFilteredItems(result);
  }, [searchQuery, selectedCategoryIds, selectedTypeIds, fuse, items]);

  const toggleCategory = (id: string) => {
    setSelectedCategoryIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const toggleType = (id: string) => {
    setSelectedTypeIds((prev) => (prev.includes(id) ? [] : [id]));
  };

  const getSelectedLabels = (selectedIds: string[], source: FarmMetaProps[]) =>
    source
      .filter((item) => selectedIds.includes(item._id))
      .map((item) => ({
        id: item._id,
        title: item.title,
      }));

  const selectedCategoryTags = getSelectedLabels(
    selectedCategoryIds,
    sidebar.categories
  );
  const selectedTypeTags = getSelectedLabels(selectedTypeIds, sidebar.types);

  return (
    <div className="flex flex-col mdLg:flex-row gap-8 md:gap-16 mdLg:gap-24 max-w-[1070px] mx-auto justify-center">
      {/* SIDEBAR */}
      <div className="flex flex-col gap-8 md:gap-16 items-center mdLg:w-[260px]">
        <button
          className="flex stic mdLg:hidden items-center gap-2 px-4 py-2 rounded-full bg-secondaryDark text-white font-bold hover:bg-secondary hover:text-secondaryDark w-full justify-center"
          onClick={() => setIsSidebarOpen(!isSidebarOpen)}
          aria-label="Toggle Filter Sidebar"
        >
          Filter <CaretDownIcon />
        </button>
        <div
          className={
            `w-full flex-col gap-8` +
            (isSidebarOpen ? " flex" : " hidden mdLg:flex")
          }
        >
          <div className="flex flex-col w-full">
            <input
              type="text"
              className="w-full px-4 pt-[8px] pb-[7px] rounded-[30px] border-[2px] placeholder:text-primaryDark placeholder:font-bold border-primaryDark bg-white mb-8"
              placeholder="Search"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            {sidebar.categories.map((type, i) => (
              <TypeItem
                key={type._id}
                {...type}
                onClick={toggleCategory}
                active={selectedCategoryIds.includes(type._id)}
                color="text-primaryDark"
                last={i === sidebar.categories.length - 1}
              />
            ))}
          </div>
          <div className="flex flex-col w-full">
            {sidebar.types.map((type, i) => (
              <TypeItem
                key={type._id}
                {...type}
                onClick={toggleType}
                active={selectedTypeIds.includes(type._id)}
                color="text-secondaryDark"
                last={i === sidebar.types.length - 1}
              />
            ))}
          </div>
        </div>
      </div>

      {/* RESULTS */}
      <div className="flex flex-col gap-8 mdLg:w-[calc(100%-260px)]">
        {(selectedCategoryTags.length > 0 || selectedTypeTags.length > 0) && (
          <div className="flex flex-wrap gap-2 mb-4">
            {searchQuery && (
              <FilterTag
                label={`Search: "${searchQuery}"`}
                onClick={() => setSearchQuery("")}
              />
            )}
            {selectedCategoryTags.map((tag) => (
              <FilterTag
                key={tag.id}
                color="bg-secondaryDark"
                label={tag.title}
                onClick={() => toggleCategory(tag.id)}
              />
            ))}
            {selectedTypeTags.map((tag) => (
              <FilterTag
                key={tag.id}
                color="bg-tertiaryDark"
                label={tag.title}
                onClick={() => toggleType(tag.id)}
              />
            ))}
            <button
              onClick={() => {
                setSelectedCategoryIds([]);
                setSelectedTypeIds([]);
                setSearchQuery("");
                setFilteredItems(items);
                document.getElementById("searchInput")?.focus();
              }}
              className="bg-quaternary text-white px-4 py-1 rounded-full text-sm font-bold hover:bg-quaternaryDark"
            >
              Clear All ✕
            </button>
          </div>
        )}

        {filteredItems.length === 0 && (
          <p className="text-xl font-bold text-center">No farms found.</p>
        )}
        {filteredItems.map(
          (item) =>
            item.link && (
              <Link
                href={item.link}
                key={item._id || "#"}
                target="_blank"
                rel="noopener noreferrer"
                className="farmCard duration-[0.1s] bg-secondary py-[30px] sm:py-[40px] w-full px-[30px] sm:px-[60px] rounded-[20px] sm:rounded-[60px] text-secondaryDark flex flex-col gap-2 hover:bg-secondaryDark hover:text-white"
              >
                <p className="font-display text-[24px] sm:text-[35px]">
                  {item.title}
                </p>
                <p className="text-[16px] flex flex-col sm:flex-row sm:items-center sm:gap-8">
                  {item.type && (
                    <strong className="uppercase">{item.type.title}</strong>
                  )}
                  {item.county}
                </p>
                {item.categories && item.categories.length > 0 && (
                  <>
                    <hr className="border-t-white" />
                    <p>
                      Produce:{" "}
                      {item.categories?.map((cat, i) => (
                        <span key={`${cat && cat._id ? cat._id : "cat"}_${i}`}>
                          {cat?.title}
                          {i < item.categories.length - 1 ? ", " : ""}
                        </span>
                      ))}
                    </p>
                  </>
                )}
              </Link>
            )
        )}
      </div>
    </div>
  );
};
